import React, { useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  useTheme,
  Theme,
  TableSortLabel,
  Box,
  Modal,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useNavigate } from "react-router-dom";
import { faBoltLightning } from "@fortawesome/free-solid-svg-icons";
import {
  CustomMenu,
  CustomMenuItem,
  StyledTableCell,
  StyledTableRow,
} from "../../../styles/Shipmentstyles";
import { Product } from "../../../models/Products/Product";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import AppTablePagenation from "../common/pages/AppTablePagenation";
import AppButton from "../common/buttons/AppButton";
import { SortType } from "../../../models/SortType";
import { observer } from "mobx-react";

interface ProductTableProp {
  maxHeight: number;
  products: Product[];
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
  handleDuplicate?: (id: number) => void;
  onAddToPod?: (productId: number) => void;
  addToPodLoadingId?: number | null;
}

const ProductTable = ({
  maxHeight,
  products,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  handleDuplicate,
  onAddToPod,
  addToPodLoadingId,
}: ProductTableProp) => {
  const bolt_icon = (
    <FontAwesomeIcon icon={faBoltLightning} fixedWidth color="#F9B234" />
  );

  let navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedId] = useState<number[]>([]);
  const [currentProductId, setCurrentProductId] = useState<number | null>(null);
  const [selectedDescription, setSelectedDescription] = useState<string>("");
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] =
    useState<boolean>(false);

  const theme = useTheme<Theme>();

  const isOpen = Boolean(anchorEl);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    currentProductId: number
  ) => {
    setAnchorEl(event.currentTarget);
    setCurrentProductId(currentProductId);
  };

  const handleEdit = () => {
    if (currentProductId)
      navigate("/products/add-edit-product?product-id=" + currentProductId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentProductId(null);
  };

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  return (
    <>
      <Paper sx={{ marginTop: "0.6rem" }}>
        {selectedId?.length > 0 && (
          <Typography sx={{ paddingLeft: 2, paddingTop: 1 }}>
            {`Selected - ` + selectedId?.length}
          </Typography>
        )}
        <TableContainer sx={{ maxHeight: maxHeight }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell
                  sx={{
                    textAlign: "center",
                    fontSize: 14,
                    position: "sticky",
                    left: 0,
                    zIndex: 20,
                    boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                  }}
                >
                  <TableSortLabel
                    active={sortBy === "id"}
                    direction={sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        "id",
                        sortBy === "id" && sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => <span>{renderSortIcon("id")}</span>}
                    sx={{ fontSize: "12px" }}
                  >
                    ID
                  </TableSortLabel>
                </StyledTableCell>
                <AppTableHeaderCell minWidth="250px">
                  <TableSortLabel
                    active={sortBy === "name"}
                    direction={sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        "name",
                        sortBy === "name" && sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => <span>{renderSortIcon("name")}</span>}
                    sx={{ fontSize: "12px" }}
                  >
                    Name
                  </TableSortLabel>
                </AppTableHeaderCell>

                <AppTableHeaderCell title="Category" fontSize="12px" />
                <AppTableHeaderCell>
                  <TableSortLabel
                    active={sortBy === "cost_price"}
                    direction={sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        "cost_price",
                        sortBy === "cost_price" && sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => (
                      <span>{renderSortIcon("cost_price")}</span>
                    )}
                    sx={{ fontSize: "12px" }}
                  >
                    Cost Price
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell>
                  <TableSortLabel
                    active={sortBy === "selling_price"}
                    direction={sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        "selling_price",
                        sortBy === "selling_price" && sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => (
                      <span>{renderSortIcon("selling_price")}</span>
                    )}
                    sx={{ fontSize: "12px" }}
                  >
                    Selling Price
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell title="HSN" fontSize="12px" />
                <AppTableHeaderCell title="GST %" fontSize="12px" />
                <AppTableHeaderCell
                  title="Description"
                  minWidth="120px"
                  fontSize="12px"
                />
                <AppTableHeaderCell
                  title="Creator"
                  minWidth="150px"
                  fontSize="12px"
                />
                <AppTableHeaderCell>
                  <TableSortLabel
                    active={sortBy === "created_date"}
                    direction={sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        "created_date",
                        sortBy === "created_date" && sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => (
                      <span>{renderSortIcon("created_date")}</span>
                    )}
                    sx={{ fontSize: "12px" }}
                  >
                    Created Date
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  title="Modifier"
                  minWidth="150px"
                  fontSize="12px"
                />

                <AppTableHeaderCell>
                  <TableSortLabel
                    active={sortBy === "modified_date"}
                    direction={sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        "modified_date",
                        sortBy === "modified_date" && sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => (
                      <span>{renderSortIcon("modified_date")}</span>
                    )}
                    sx={{ fontSize: "12px" }}
                  >
                    Modified Date
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell>
                  <TableSortLabel
                    active={sortBy === "active"}
                    direction={sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        "active",
                        sortBy === "active" && sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => (
                      <span>{renderSortIcon("active")}</span>
                    )}
                    sx={{ fontSize: "12px" }}
                  >
                    Active
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  title="Display"
                  minWidth="100px"
                  fontSize="12px"
                />
                {onAddToPod !== undefined ? (
                  <AppTableHeaderCell
                    title="Add to POD"
                    minWidth="100px"
                    fontSize="12px"
                  />
                ) : null}
                <AppTableHeaderCell
                  title="Actions"
                  minWidth="100px"
                  fontSize="12px"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    position: "sticky",
                    zIndex: 10,
                    minWidth: "80px",
                    right: 0,
                  }}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product: Product) => (
                <StyledTableRow
                  key={product.id}
                  selected={selectedId.includes(product.id!)}
                >
                  <TableCell
                    sx={{
                      fontSize: "12px",
                      textAlign: "center",
                      left: 0,
                      position: "sticky",
                      zIndex: 15,
                      boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                      backgroundColor: `${theme.palette.background.paper}`,
                    }}
                  >
                    <Link
                      to={`/products/add-edit-product?product-id=${product.id}`}
                    >
                      {product.id}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    <Box display="flex" alignItems="center">
                      <Box
                        sx={{
                          position: "relative",
                          background: "rgba(242, 242, 242, 1)",
                          padding: 1,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          marginRight: 2,
                          minHeight: "70px",
                          maxHeight: "70px",
                          borderRadius: "4px",
                        }}
                      >
                        <img
                          style={{
                            maxHeight: "55px",
                            minHeight: "55px",
                            maxWidth: "55px",
                            minWidth: "55px",
                          }}
                          src={product.image_url}
                          alt={product.name}
                        />
                      </Box>
                      <Box sx={{ textAlign: "left" }}>
                        {product.name}{" "}
                        {product.fast_procurement ? bolt_icon : ""}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {product.categories
                      ?.map((cat) => cat.category_name)
                      .join(", ") || "N/A"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {product.cost_price || "-"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {product.selling_price}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {product.hsn_code}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {product.gst_percentage}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {product.desc ? (
                      <AppButton
                        label="View Desc"
                        variant="transparentPrimary"
                        customStyles={{
                          fontSize: "0.8rem",
                          padding: "4px 8px",
                          minWidth: "auto",
                        }}
                        onClick={() => {
                          setSelectedDescription(product.desc || "");
                          setIsDescriptionModalOpen(true);
                        }}
                      />
                    ) : (
                      "--"
                    )}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {product.creator_name}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {product.created_date
                      ? new Date(product.created_date).toLocaleDateString(
                          "en-GB",
                          {
                            day: "numeric",
                            month: "numeric",
                            year: "numeric",
                          }
                        )
                      : "NA"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {product.modifier_name}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {product.modified_date
                      ? new Date(product.modified_date).toLocaleDateString(
                          "en-GB",
                          {
                            day: "numeric",
                            month: "numeric",
                            year: "numeric",
                          }
                        )
                      : "-"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {Boolean(product.active) ? "True" : "False"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {Boolean(product.display) ? "True" : "False"}
                  </TableCell>
                  {onAddToPod !== undefined ? (
                    <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                      {(() => {
                        const inPodCatalog = Boolean(product.in_pod_catalog);
                        return (
                      <AppButton
                        label="Add"
                        variant="primary"
                        disabled={
                          addToPodLoadingId === product.id || inPodCatalog
                        }
                        customStyles={{
                          fontSize: "0.75rem",
                          padding: "4px 12px",
                          minWidth: "auto",
                          height: 32,
                          opacity: inPodCatalog ? 0.6 : 1,
                        }}
                        onClick={() => product.id != null && onAddToPod(product.id)}
                      />
                        );
                      })()}
                    </TableCell>
                  ) : null}
                  <TableCell
                    sx={{
                      width: "10%",
                      fontSize: "12px",
                      color: theme.palette.customColors.tableGrayHeadingColor,
                      position: "sticky",
                      zIndex: 10,
                      minWidth: "80px",
                      right: 0,
                      background: theme.palette.customColors.white,
                    }}
                  >
                    <IconButton
                      onClick={(event) => handleMenuClick(event, product.id!)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <CustomMenu
                      anchorEl={anchorEl}
                      onClose={handleMenuClose}
                      open={isOpen}
                      anchorOrigin={{ vertical: "top", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <CustomMenuItem onClick={() => handleEdit()}>
                        <Link
                          to={`/products/add-edit-product?product-id=${currentProductId}`}
                          style={{ textDecoration: "none", color: "black" }}
                        >
                          View/Edit
                        </Link>
                      </CustomMenuItem>
                      <CustomMenuItem
                        onClick={() => {
                          if (handleDuplicate) {
                            handleDuplicate(currentProductId!);
                            handleMenuClose();
                          }
                        }}
                      >
                        Duplicate
                      </CustomMenuItem>
                      {/* <CustomMenuItem>
                        <FolderCopyIcon sx={{ marginRight: 1 }} />
                        Upload
                      </CustomMenuItem> */}
                    </CustomMenu>
                  </TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <AppTablePagenation
          fontSize="12px"
          pageNo={page_no}
          limit={limit}
          totalCount={total_count}
          onPageChange={onPageChange}
          handleRowsChange={handleRowsChange}
        />
      </Paper>

      {/* Description Modal */}
      <Modal
        open={isDescriptionModalOpen}
        onClose={() => setIsDescriptionModalOpen(false)}
        aria-labelledby="description-modal-title"
        aria-describedby="description-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 800,
            maxHeight: "80vh",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Product Description
            </Typography>
            <IconButton
              onClick={() => setIsDescriptionModalOpen(false)}
              sx={{
                color: theme.palette.grey[500],
                "&:hover": {
                  color: theme.palette.grey[700],
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              backgroundColor: theme.palette.grey[50],
              p: 2,
              borderRadius: 1,
              border: `1px solid ${theme.palette.grey[200]}`,
              maxHeight: "60vh",
              overflow: "auto",
            }}
          >
            <Box
              dangerouslySetInnerHTML={{
                __html: selectedDescription || "No description available",
              }}
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "14px",
                lineHeight: "1.6",
                "& h1, & h2, & h3, & h4, & h5, & h6": {
                  marginTop: "16px",
                  marginBottom: "8px",
                },
                "& p": {
                  marginBottom: "12px",
                },
                "& ul, & ol": {
                  marginBottom: "12px",
                  paddingLeft: "20px",
                },
                "& li": {
                  marginBottom: "4px",
                },
              }}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default observer(ProductTable);
